import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { useSupportStore } from '../state/use-support-store';
import MessageBubble from './MessageBubble';
import LoadingSpinner from '../../../components/LoadingSpinner';

const NEAR_BOTTOM_PX = 80;
const LOAD_OLDER_PX = 48;

const assignScrollTop = (el, value, autoScrollRef) => {
  autoScrollRef.current = true;
  el.scrollTop = value;
  requestAnimationFrame(() => {
    autoScrollRef.current = false;
  });
};

const MessageList = () => {
  const { messages, messagesLoading, messagesError, loadingOlder, hasMoreMessages, loadOlderMessages, scrollToken, selectedId: threadId } = useSupportStore();

  const scrollerRef = useRef(null);
  const contentRef = useRef(null);
  const pinnedRef = useRef(true);
  const restoreHeightRef = useRef(null);
  const armedRef = useRef(true);
  const autoScrollRef = useRef(false);

  const scrollToBottom = () => {
    const el = scrollerRef.current;
    if (!el) return;
    assignScrollTop(el, el.scrollHeight, autoScrollRef);
  };

  useLayoutEffect(() => {
    pinnedRef.current = true;
    restoreHeightRef.current = null;
    armedRef.current = true;
  }, [threadId]);

  useLayoutEffect(() => {
    if (!scrollToken) return;
    pinnedRef.current = true;
  }, [scrollToken]);

  useLayoutEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;

    if (restoreHeightRef.current != null) {
      const previousHeight = restoreHeightRef.current;
      restoreHeightRef.current = null;
      assignScrollTop(el, el.scrollHeight - previousHeight, autoScrollRef);
      return;
    }

    if (pinnedRef.current) scrollToBottom();
  }, [messages, threadId, scrollToken]);

  useEffect(() => {
    const content = contentRef.current;
    const el = scrollerRef.current;
    if (!content || !el || typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver(() => {
      if (!pinnedRef.current || restoreHeightRef.current != null) return;
      assignScrollTop(el, el.scrollHeight, autoScrollRef);
    });

    observer.observe(content);
    return () => observer.disconnect();
  }, [threadId, messagesLoading]);

  const requestOlder = () => {
    const el = scrollerRef.current;
    restoreHeightRef.current = el?.scrollHeight || 0;
    loadOlderMessages().then((prepended) => {
      if (!prepended) restoreHeightRef.current = null;
    });
  };

  const handleScroll = () => {
    const el = scrollerRef.current;
    if (!el || autoScrollRef.current) return;

    pinnedRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_PX;
    if (el.scrollTop > NEAR_BOTTOM_PX) armedRef.current = true;
    if (el.scrollTop > LOAD_OLDER_PX || !armedRef.current || !hasMoreMessages || loadingOlder) return;

    armedRef.current = false;
    requestOlder();
  };

  if (messagesLoading) return <LoadingSpinner loading style="flex flex-1 items-center justify-center" />;

  if (messagesError?.error && !messages.length) {
    return <p className="m-auto px-6 text-center text-sm text-red-500">{messagesError.error}</p>;
  }

  return (
    <div ref={scrollerRef} onScroll={handleScroll} className="min-h-0 flex-1 overflow-y-auto bg-white">
      <div ref={contentRef} className="flex min-h-full flex-col gap-3 px-4 py-4">
        {hasMoreMessages ? (
          <button type="button" disabled={loadingOlder} onClick={requestOlder} className="self-center text-sm text-orange-600 disabled:text-gray-400">
            {loadingOlder ? 'Загрузка…' : 'Загрузить предыдущие'}
          </button>
        ) : null}

        {!messages.length ? <p className="m-auto text-center text-sm text-gray-500">Пользователь ещё ничего не написал</p> : null}

        {messages.map((message) => (
          <MessageBubble key={message.id} message={message} />
        ))}
      </div>
    </div>
  );
};

export default MessageList;
